/* eslint-disable no-magic-numbers */
/* eslint-disable no-param-reassign */
import { pluginSchema } from '@/models/pluginSchema';
import { axiosInstance } from '@/services/api/utils/axiosInstance';
import type { MinervaPlugin } from '@/types/models';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { HttpStatusCode } from 'axios';
import { ThunkConfig } from '@/types/store';
import { getPluginHashWithoutPrefix } from '@/utils/plugins/getPluginHashWithoutPrefix';
import { getError } from '@/utils/error-report/getError';
import { validatePluginContent } from '@/components/admin/utils';
import { showToast } from '@/utils/showToast';
import { apiPath } from '../apiPath';
import {
  PLUGIN_FETCHING_ALL_ERROR_PREFIX,
  PLUGIN_INIT_FETCHING_ERROR_PREFIX,
  PLUGIN_REGISTER_ERROR_PREFIX,
} from './plugins.constants';

type RegisterPlugin = {
  hash: string;
  pluginUrl: string;
  pluginName: string;
  pluginVersion: string;
  isPublic: boolean;
  extendedPluginName: string;
  withoutPanel: boolean | undefined;
};

export const registerPlugin = createAsyncThunk<
  MinervaPlugin | undefined,
  RegisterPlugin,
  ThunkConfig
>(
  'plugins/registerPlugin',
  async ({
    hash,
    isPublic,
    pluginName,
    pluginUrl,
    pluginVersion,
    extendedPluginName,
    withoutPanel,
  }) => {
    try {
      const hashWithoutPrefix = getPluginHashWithoutPrefix(hash);

      const payload = {
        hash: hashWithoutPrefix,
        url: pluginUrl,
        name: pluginName,
        version: pluginVersion,
        isPublic: isPublic.toString(),
      } as const;

      const response = await axiosInstance.post<MinervaPlugin>(
        apiPath.registerPluign(),
        new URLSearchParams(payload),
        {
          withCredentials: true,
        },
      );

      const isDataValid = validateDataUsingZodSchema(response.data, pluginSchema);

      if (isDataValid) {
        return {
          ...response.data,
          hash,
          name: extendedPluginName,
          withoutPanel,
        };
      }

      return undefined;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PLUGIN_REGISTER_ERROR_PREFIX }));
    }
  },
);

type GetInitPluginsProps = {
  pluginsId: string[];
  setHashedPlugin: ({
    pluginUrl,
    pluginScript,
  }: {
    pluginUrl: string;
    pluginScript: string;
  }) => void;
};

async function getPluginByHash(hash: string): Promise<MinervaPlugin | null> {
  try {
    const res = await axiosInstance<MinervaPlugin>(apiPath.getPlugin(hash));
    const isDataValid = validateDataUsingZodSchema(res.data, pluginSchema);

    if (isDataValid) {
      return res.data;
    }
    return null;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error?.response?.status === HttpStatusCode.NotFound) {
        return null;
      }
    }
    throw error;
  }
}

export const getInitPlugins = createAsyncThunk<void, GetInitPluginsProps, ThunkConfig>(
  'plugins/getInitPlugins',
  // eslint-disable-next-line consistent-return
  async ({ pluginsId, setHashedPlugin }) => {
    try {
      /* eslint-disable no-restricted-syntax, no-await-in-loop */

      for (const pluginId of pluginsId) {
        const hash = getPluginHashWithoutPrefix(pluginId);

        const plugin = await getPluginByHash(hash);

        if (plugin) {
          const { urls } = plugin;
          const scriptRes = await axios(urls[0]);

          let pluginScript = scriptRes.data;
          setHashedPlugin({ pluginUrl: urls[0], pluginScript });

          pluginScript += `//# sourceURL=${urls[0]}`;

          /* eslint-disable no-new-func */
          const loadPlugin = new Function(pluginScript);

          loadPlugin();
        } else {
          showToast({ type: 'error', message: `Problem with loading plugin: ${hash}` });
        }
      }
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PLUGIN_INIT_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const getAllPlugins = createAsyncThunk<MinervaPlugin[], void, ThunkConfig>(
  'plugins/getAllPlugins',
  async () => {
    try {
      const response = await axiosInstance.get<MinervaPlugin[]>(apiPath.getAllPlugins());

      const isPluginDataValid = (pluginData: MinervaPlugin): boolean =>
        validateDataUsingZodSchema(pluginData, pluginSchema);
      const validPlugins = response.data.filter(isPluginDataValid);

      return validPlugins;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PLUGIN_FETCHING_ALL_ERROR_PREFIX }));
    }
  },
);

export const revalidatePlugin = createAsyncThunk<MinervaPlugin, string, ThunkConfig>(
  'plugins/revalidatePlugin',
  async (hash: string) => {
    try {
      // First get the current plugin info to find the source URL
      const currentPluginResponse = await axiosInstance.get<MinervaPlugin>(apiPath.getPlugin(hash));
      const plugin = currentPluginResponse.data;

      if (!plugin.urls || plugin.urls.length === 0) {
        throw new Error('Plugin has no source URL');
      }

      const sourceUrl = plugin.urls[0];

      // Extract fresh info from source
      const { name, version } = await validatePluginContent(sourceUrl);

      // Update via existing POST endpoint (same hash = update)
      const formData = new URLSearchParams({
        hash,
        name,
        version,
        url: sourceUrl,
        isPublic: plugin.isPublic.toString(),
        isDefault: plugin.isDefault.toString(),
      });

      const response = await axiosInstance.post<MinervaPlugin>(apiPath.addPlugin(), formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      return response.data;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: 'Failed to revalidate plugin' }));
    }
  },
);

export const removePlugin = createAsyncThunk<boolean, string, ThunkConfig>(
  'plugins/removePlugin',
  async (hash: string) => {
    try {
      await axiosInstance.delete(apiPath.removePlugin(hash));
      return true;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: 'Failed to remove plugin' }));
    }
  },
);

export const addPlugin = createAsyncThunk<
  boolean,
  {
    hash: string;
    name: string;
    version: string;
    url: string;
  },
  ThunkConfig
>('plugins/addPlugin', async ({ hash, name, version, url }) => {
  try {
    const formData = new URLSearchParams({
      hash,
      name,
      version,
      url,
      isPublic: 'true',
    });

    await axiosInstance.post(apiPath.addPlugin(), formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return true;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: 'Failed to add plugin' }));
  }
});

export const updatePlugin = createAsyncThunk<
  boolean,
  {
    hash: string;
    isDefault: boolean;
  },
  ThunkConfig
>('plugins/updatePlugin', async ({ hash, isDefault }) => {
  try {
    await axiosInstance.patch(
      apiPath.getPlugin(hash),
      { plugin: { hash, isDefault } },
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return true;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: 'Failed to update plugin' }));
  }
});
